import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { AmbassadorProvider } from "./context/AmbassadorContext";

// Landing page components
import HomepageImage from "./components/HomepageImage";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import FastByNoFeeSection from "./components/FastByNoFeeSection";
import FastSpotlightSection from "./components/FastSpotlightSection";
import DirectPaymentsSection from "./components/DirectPaymentsSection";
import RewardsAmbassadorSection from "./components/RewardsAmbassadorSection";
import HomepageCategories from "./components/HomepageCategories";
import DiscoveryAndPopular from "./components/DiscoveryAndPopular";
import TrustedReviewsSection from "./components/TrustedReviewsSection";
import HowItWorksV2Section from "./components/HowItWorksV2Section";
import FoundingPricingSection from "./components/FoundingPricingSection";
import BuiltForModernRestaurants from "./components/BuiltForModernRestaurants";
import FinalCTASection from "./components/FinalCTASection";
import FAQSection from "./components/FAQSection";
import FooterSection from "./components/FooterSection";

// App pages
import LoginPage from "./pages/LoginPage";
import RestaurantMagicLink from "./pages/RestaurantMagicLink";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import PublicMenu from "./pages/PublicMenu";
import RestaurantsDirectory from "./pages/RestaurantsDirectory";
import ForRestaurants from "./pages/ForRestaurants";
import ForCustomers from "./pages/ForCustomers";
import NoFeeOrdering from "./pages/NoFeeOrdering";
import NoFeePOSLite from "./pages/NoFeePOSLite";
import SavingsCalculator from "./pages/SavingsCalculator";
import WhyNoFee from "./pages/WhyNoFee";
import CompleteSubscriptionPage from "./pages/CompleteSubscriptionPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import PendingRestaurantsAdmin from "./pages/PendingRestaurantsAdmin";
import GoLiveEngagementAdmin from "./pages/GoLiveEngagementAdmin";
import StripeHealthAdmin from "./pages/StripeHealthAdmin";
import DashboardGiftsPage from "./pages/DashboardGiftsPage";
import GrowMyRestaurant from "./pages/GrowMyRestaurant";
import ReimbursementsAdmin from "./pages/ReimbursementsAdmin";
import StartSignupPage from "./pages/StartSignupPage";
import CategoryPage from "./pages/CategoryPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import OnboardingWizard from "./pages/OnboardingWizard";
import OnboardingHubPage from "./pages/OnboardingHubPage";
import OnboardingPayoutsPage from "./pages/OnboardingPayoutsPage";
import OnboardingStripeConnectPage from "./pages/OnboardingStripeConnectPage";
import OnboardingStripeReturnPage from "./pages/OnboardingStripeReturnPage";
import OnboardingPlanPage from "./pages/OnboardingPlanPage";
import OnboardingTrialPage from "./pages/OnboardingTrialPage";
import OnboardingAccountPage from "./pages/OnboardingAccountPage";
import OnboardingRedirect from "./pages/OnboardingRedirect";
import OnboardingSetupPage from "./pages/OnboardingSetupPage";
import OrderFeedback from "./pages/OrderFeedback";
import CustomerOnboarding from "./pages/CustomerOnboarding";
import CustomerRewards from "./pages/CustomerRewards";
import CustomerMagicAuth from "./pages/CustomerMagicAuth";
import ReferralLanding from "./pages/ReferralLanding";
import RewardsRedirect from "./pages/RewardsRedirect";
import RestaurantReferralLanding from "./pages/RestaurantReferralLanding";
import AboutUs from "./pages/AboutUs";
import ContactPage from "./pages/ContactPage";
import SupportAdminPage from "./pages/SupportAdminPage";
import AdminHubPage from "./pages/AdminHubPage";
import SubscriptionsAdminPage from "./pages/SubscriptionsAdminPage";
import RevenueAdminPage from "./pages/RevenueAdminPage";

// Customer Account Center (Dashboard, Rewards, Favorites, Orders, Referrals, Notifications, Settings)
import AccountDashboard from "./pages/account/AccountDashboard";
import AccountRewards from "./pages/account/AccountRewards";
import AccountFavorites from "./pages/account/AccountFavorites";
import AccountOrders from "./pages/account/AccountOrders";
import AccountReferrals from "./pages/account/AccountReferrals";
// iter86 (Jun 2026): AccountNotifications page retired. Bell in the
// customer account topbar is now a popover; reward ledger moved to
// /account/rewards/statement.
import AccountRewardsStatement from "./pages/account/AccountRewardsStatement";
import AccountSettings from "./pages/account/AccountSettings";

// Rewards pages
import RewardsLanding from "./pages/RewardsLanding";
import OrderReceipt from "./pages/OrderReceipt";
import OrderKitchenTicket from "./pages/OrderKitchenTicket";

// Partners Portal (associations, chambers, influencers, agencies)
import PartnersLanding from "./pages/PartnersLanding";
import PartnersOnboarding from "./pages/PartnersOnboarding";
// iter100 — Blog scaffolding (P9)
import BlogIndex from "./pages/blog/BlogIndex";
import BlogPost from "./pages/blog/BlogPost";
import PartnersLogin from "./pages/PartnersLogin";
// iter98 — Partner Account portal (sidebar-driven, mirrors customer "My Account")
import PartnerDashboardHome from "./pages/partners/account/PartnerDashboardHome";
import PartnerProfile from "./pages/partners/account/PartnerProfile";
import PartnerReferralLink from "./pages/partners/account/PartnerReferralLink";
import PartnerMemberRestaurants from "./pages/partners/account/PartnerMemberRestaurants";
import PartnerCommissions from "./pages/partners/account/PartnerCommissions";
import PartnerStripePayouts from "./pages/partners/account/PartnerStripePayouts";
import PartnerMarketingCenter from "./pages/partners/account/PartnerMarketingCenter";
import PartnersPartnerBenefits from "./pages/PartnersPartnerBenefits";
import PartnersMemberBenefits from "./pages/PartnersMemberBenefits";
import PartnersScheduleDemo from "./pages/PartnersScheduleDemo";
// iter93 — Partners Support Center (Help Center / Ticket / Contact)
import PartnersHelpCenter from "./pages/partners/PartnersHelpCenter";
import PartnersSubmitTicket from "./pages/partners/PartnersSubmitTicket";
import PartnersContactSupport from "./pages/partners/PartnersContactSupport";
import {
  RewardsSignupRedirect,
  RewardsLoginRedirect,
} from "./pages/RewardsAuthRedirects";
import AmbassadorDashboard from "./pages/AmbassadorDashboard";
import RewardsCenter from "./pages/RewardsCenter";

// Legal pages
import TermsOfService from "./pages/legal/TermsOfService";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import CookiePolicy from "./pages/legal/CookiePolicy";
import RewardsTerms from "./pages/legal/RewardsTerms";
import RestaurantAgreement from "./pages/legal/RestaurantAgreement";
import PartnerAgreement from "./pages/legal/PartnerAgreement";
import CustomerAgreement from "./pages/legal/CustomerAgreement";
import RefundPolicy from "./pages/legal/RefundPolicy";

// Compliance
import CookieBanner from "./components/CookieBanner";
import SEO from "./components/SEO";
import StickyMobileCTA from "./components/StickyMobileCTA";
import InstallAppPrompt from "./components/InstallAppPrompt";

// Toaster (sonner)
import { Toaster } from "./components/ui/sonner";

// ──────────────────────────────────────────────────────────────────────
// iter120 — Legacy `/start` redirect to the new Step 1 wizard.
//
// Background: 11+ CTAs across the marketing site (homepage hero, sticky
// mobile, About Us, NoFee Ordering, Why NoFee, For Restaurants, Savings
// Calculator, etc.) still link to `/start?plan=ordering`. Before iter116
// that route was the old StartSignupPage (light theme, 4 fields). Owner
// wants every entry point to the signup flow to land on the NEW
// `/onboarding/account` form (Step 1 — Restaurant Name, Owner, Email,
// Phone, Password, Confirm, Website, Address). Touching 11 files is
// risky and easy to miss future links — so we redirect the route
// itself, preserving query params (?plan=, ?partner=, ?utm_*).
// ──────────────────────────────────────────────────────────────────────
function LegacyStartRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`/onboarding/account${location.search || ''}${location.hash || ''}`}
      replace
    />
  );
}

const LandingPage = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://nofee.restaurant/#org",
        "name": "NoFee",
        "alternateName": "NoFee.restaurant",
        "url": "https://nofee.restaurant",
        "logo": "https://nofee.restaurant/brand/og-default.jpg",
        "sameAs": [],
        "description": "0% commission restaurant ordering platform. Direct Stripe payouts to restaurants."
      },
      {
        "@type": "WebSite",
        "@id": "https://nofee.restaurant/#website",
        "url": "https://nofee.restaurant",
        "name": "NoFee",
        "publisher": { "@id": "https://nofee.restaurant/#org" },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://nofee.restaurant/restaurants?zip={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "SoftwareApplication",
        "@id": "https://nofee.restaurant/#software",
        "name": "NoFee",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "url": "https://nofee.restaurant",
        "description": "Commission-free online ordering, digital menus, and direct payouts for restaurants. Flat monthly subscription with 0% commission on every order.",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "description": "0% commission on every order. Restaurant subscription only."
        },
        "publisher": { "@id": "https://nofee.restaurant/#org" }
      },
      {
        "@type": "FAQPage",
        "@id": "https://nofee.restaurant/#faq",
        "mainEntity": [
          { "@type": "Question", "name": "How does NoFee work?",
            "acceptedAnswer": { "@type": "Answer", "text": "NoFee provides your restaurant with a custom digital menu accessible via QR code, link, SMS or WhatsApp. Orders come directly to you and payments go straight to your bank account via Stripe — no middleman, no commissions." } },
          { "@type": "Question", "name": "Is there really 0% commission?",
            "acceptedAnswer": { "@type": "Answer", "text": "Yes. NoFee charges a flat monthly subscription, never a commission on orders. Stripe's standard 2.9% + 30¢ processing fee still applies but there are no NoFee fees on top of that." } },
          { "@type": "Question", "name": "How fast can a restaurant get live on NoFee?",
            "acceptedAnswer": { "@type": "Answer", "text": "Most restaurants are fully live within 24 hours. Upload your menu, connect Stripe, and you can start taking direct orders the same day." } },
          { "@type": "Question", "name": "Do customers need to download an app?",
            "acceptedAnswer": { "@type": "Answer", "text": "No. NoFee runs in any web browser — customers scan a QR code or click a link, browse the menu and order. No app downloads or accounts required." } },
          { "@type": "Question", "name": "How does NoFee compare to other ordering platforms?",
            "acceptedAnswer": { "@type": "Answer", "text": "Most food-delivery marketplaces charge a per-order commission. NoFee charges a flat monthly subscription with 0% commission, so the restaurant keeps the entire ticket minus standard payment processing fees." } }
        ]
      }
    ]
  };
  return (
    <LanguageProvider>
      <SEO
        title="NoFee — 0% Commission Restaurant Ordering Platform"
        description="NoFee is the 0% commission restaurant ordering platform. Direct Stripe payouts — better prices for customers, more profit for restaurants."
        path="/"
        brand={false}
        jsonLd={jsonLd}
      />
      <Navbar />
      <HeroSection />
      <FastByNoFeeSection />
      <FastSpotlightSection />
      <DirectPaymentsSection />
      <RewardsAmbassadorSection />
      <HomepageCategories />
      <DiscoveryAndPopular />
      <TrustedReviewsSection />
      <HowItWorksV2Section />
      <BuiltForModernRestaurants />
      <FoundingPricingSection />
      <FAQSection />
      <FinalCTASection />
      <FooterSection />
    </LanguageProvider>
  );
};

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-black text-white p-8 text-center">
          <div>
            <h1 className="text-3xl font-black mb-3 text-gold-400">Something went wrong</h1>
            <p className="text-white/60 mb-6">Our team has been notified. Please refresh the page.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-xl"
            >
              Reload
            </button>
          </div>
        </div>
      }
    >
      <AuthProvider>
        <AmbassadorProvider>
          <HelmetProvider>
            <div className="App">
              <BrowserRouter>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/customer" element={<CustomerOnboarding />} />
                <Route path="/customer/signup" element={<CustomerOnboarding />} />
                <Route path="/customer/rewards" element={<CustomerRewards />} />
                <Route path="/customer/auth/magic" element={<CustomerMagicAuth />} />
                {/* Customer Account Center */}
                <Route path="/account" element={<AccountDashboard />} />
                <Route path="/account/rewards" element={<AccountRewards />} />
                <Route path="/account/favorites" element={<AccountFavorites />} />
                <Route path="/account/orders" element={<AccountOrders />} />
                <Route path="/account/referrals" element={<AccountReferrals />} />
                <Route path="/account/notifications" element={<Navigate to="/account/rewards/statement" replace />} />
                <Route path="/account/rewards/statement" element={<AccountRewardsStatement />} />
                <Route path="/account/settings" element={<AccountSettings />} />
                <Route path="/r/:code" element={<ReferralLanding />} />
                {/* 2026-02 new referral & share-tracking shapes. Persist
                    attribution in localStorage then bounce to the rich
                    invite landing (/r/:code) or the restaurant onboarding
                    funnel with ?ref=:code so the rewards engine can
                    attribute the eventual signup back to the inviter. */}
                <Route path="/ref/friend/:code"        element={<RewardsRedirect kind="friend" />} />
                {/* Restaurant referral lands on a dedicated VALUE-FIRST page
                    instead of the old behavior of dropping owners straight
                    onto the pricing screen. Reduces onboarding abandonment
                    (Feb 2026 redesign). */}
                <Route path="/ref/restaurant/:code"    element={<RestaurantReferralLanding />} />
                <Route path="/share/:platform/:code"   element={<RewardsRedirect kind="share" />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/support" element={<ContactPage />} />
                <Route path="/help" element={<ContactPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/restaurant/auth/magic" element={<RestaurantMagicLink />} />
                <Route path="/register" element={<LegacyStartRedirect />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/complete-subscription" element={<CompleteSubscriptionPage />} />
                <Route path="/onboarding/subscription-success" element={<SubscriptionSuccessPage />} />
                <Route path="/admin/pending-restaurants" element={<PendingRestaurantsAdmin />} />
                <Route path="/admin/engagement" element={<GoLiveEngagementAdmin />} />
                <Route path="/admin/stripe-health" element={<StripeHealthAdmin />} />
                <Route path="/dashboard/gifts" element={<DashboardGiftsPage />} />
                <Route path="/dashboard/grow" element={<GrowMyRestaurant />} />
                <Route path="/admin/reimbursements" element={<ReimbursementsAdmin />} />
                <Route path="/admin/support" element={<SupportAdminPage />} />
                <Route path="/admin" element={<AdminHubPage />} />
                <Route path="/admin/subscriptions" element={<SubscriptionsAdminPage />} />
                <Route path="/admin/revenue" element={<RevenueAdminPage />} />
                <Route path="/onboarding" element={<OnboardingRedirect />} />
                <Route path="/onboarding/account" element={<OnboardingAccountPage />} />
                <Route path="/onboarding/wizard" element={<OnboardingWizard />} />
                <Route path="/onboarding/payouts" element={<OnboardingPayoutsPage />} />
                <Route path="/onboarding/stripe-connect" element={<OnboardingStripeConnectPage />} />
                <Route path="/onboarding/stripe-return" element={<OnboardingStripeReturnPage />} />
                <Route path="/onboarding/plan" element={<OnboardingPlanPage />} />
                <Route path="/onboarding/trial" element={<OnboardingTrialPage />} />
                <Route path="/onboarding/setup" element={<OnboardingSetupPage />} />
                <Route path="/restaurants" element={<RestaurantsDirectory />} />
                <Route path="/for-restaurants" element={<ForRestaurants />} />
                <Route path="/for-customers" element={<ForCustomers />} />
                <Route path="/for-restaurants/ordering" element={<NoFeeOrdering />} />
                <Route path="/for-restaurants/pos-lite" element={<NoFeePOSLite />} />
                <Route path="/for-restaurants/why-nofee" element={<WhyNoFee />} />
                <Route path="/for-restaurants/savings" element={<SavingsCalculator />} />
                <Route path="/savings-calculator" element={<SavingsCalculator />} />
                <Route path="/start" element={<LegacyStartRedirect />} />
                <Route path="/category/:slug" element={<CategoryPage />} />
                <Route path="/menu/:slug" element={<PublicMenu />} />
                <Route path="/menu/:slug/payment-success" element={<PaymentSuccess />} />
                <Route path="/order/:id/feedback" element={<OrderFeedback />} />
                <Route path="/rewards" element={<RewardsLanding />} />
                <Route path="/orders/:orderId/receipt" element={<OrderReceipt />} />
                <Route path="/orders/:orderId/kitchen" element={<OrderKitchenTicket />} />
                <Route path="/rewards/signup" element={<RewardsSignupRedirect />} />
                <Route path="/rewards/login" element={<RewardsLoginRedirect />} />
                <Route path="/rewards/dashboard" element={<RewardsCenter />} />
                <Route path="/rewards/ambassador" element={<AmbassadorDashboard />} />

                {/* Partners Portal (Phase 1 + 2) */}
                <Route path="/partners" element={<PartnersLanding />} />
                <Route path="/partners/onboarding" element={<PartnersOnboarding />} />
                <Route path="/partners/login" element={<PartnersLogin />} />
                <Route path="/partners/dashboard" element={<PartnerDashboardHome />} />
                {/* iter100 — Blog (P9) */}
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                {/* iter98 — Partner Account sub-pages (sidebar) */}
                <Route path="/partners/account/profile"     element={<PartnerProfile />} />
                <Route path="/partners/account/referral"    element={<PartnerReferralLink />} />
                <Route path="/partners/account/restaurants" element={<PartnerMemberRestaurants />} />
                <Route path="/partners/account/commissions" element={<PartnerCommissions />} />
                <Route path="/partners/account/stripe"      element={<PartnerStripePayouts />} />
                <Route path="/partners/account/marketing"   element={<PartnerMarketingCenter />} />
                <Route path="/partners/partner-benefits" element={<PartnersPartnerBenefits />} />
                <Route path="/partners/member-benefits" element={<PartnersMemberBenefits />} />
                <Route path="/partners/schedule-demo" element={<PartnersScheduleDemo />} />
                {/* iter93 — Support Center */}
                <Route path="/partners/help" element={<PartnersHelpCenter />} />
                <Route path="/partners/support/ticket" element={<PartnersSubmitTicket />} />
                <Route path="/partners/support/contact" element={<PartnersContactSupport />} />

                {/* Legal */}
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/rewards-terms" element={<RewardsTerms />} />
                <Route path="/restaurant-agreement" element={<RestaurantAgreement />} />
                <Route path="/partner-agreement" element={<PartnerAgreement />} />
                <Route path="/customer-agreement" element={<CustomerAgreement />} />
                <Route path="/refund" element={<RefundPolicy />} />
              </Routes>
              <CookieBanner />
              <StickyMobileCTA />
              <InstallAppPrompt />
              <Toaster richColors position="top-right" />
            </BrowserRouter>
            </div>
          </HelmetProvider>
        </AmbassadorProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
